// NUVUN static catalog — 6 product types, factory-built modular architecture.
// Fully static; no API needed for catalog data.

export const PRODUCT_CATEGORIES = [
  {
    num: "01",
    slug: "prefab-homes",
    name: "Prefab Homes",
    tagline: "Modern homes, delivered faster.",
    summary:
      "Factory-finished studios, 1–4 BHK homes, A-frames and barn houses — precision-built for landowners, farmhouses and second-home buyers.",
    hero_image: "/images/gen/hero-prefab-homes.jpg",
  },
  {
    num: "02",
    slug: "precast-villas",
    name: "Precast Villas",
    tagline: "Strength of concrete. Beauty of design.",
    summary:
      "High-performance precast and panelized villa systems — civil-grade permanence with factory speed for estates and communities.",
    hero_image: "/images/gen/hero-precast-villas.jpg",
  },
  {
    num: "03",
    slug: "space-capsules",
    name: "Space Capsules",
    tagline: "Compact spaces. Limitless possibilities.",
    summary:
      "Futuristic capsule pods with panoramic glass — engineered for glamping, stargazing stays and premium view-led inventory.",
    hero_image: "/images/gen/space-capsule-cat-07.jpg",
  },
  {
    num: "04",
    slug: "apple-pods",
    name: "Apple Pods",
    tagline: "Sleek. Smart. Seamlessly livable.",
    summary:
      "Rounded micro-pods with signature circular glazing — garden studios, spa rooms and work pods that install in days.",
    hero_image: "/images/gen/cat-apple-pods.jpg",
  },
  {
    num: "05",
    slug: "commercial-pods",
    name: "Commercial Pods",
    tagline: "Workspaces that move with you.",
    summary:
      "Offices, cafés, retail kiosks, clinics and site infrastructure — brandable modules for rapid commercial rollout.",
    hero_image: "/images/gen/commercial-pod-07.jpg",
  },
  {
    num: "06",
    slug: "hospitality-resort",
    name: "Hospitality & Resort",
    tagline: "Spaces guests never forget.",
    summary:
      "Resort room clusters, glamping villages, floating suites and wellness pavilions — inventory the experience economy runs on.",
    hero_image: "/images/gen/hospitality-09.jpg",
  },
];

export const PRODUCTS = [
  // ================= 01 · PREFAB HOMES =================
  {
    code: "NV-PH-01", slug: "studio-prefab-home", name: "Studio Prefab Home", category: "prefab-homes",
    size_range: "250 – 450 sq ft", form: "Studio", price_range: "₹12 L – ₹22 L", deployment: "45–60 days",
    use_cases: ["Single-person home", "Rental unit", "Compact farmhouse"], segments: ["B2C"],
    image: "/images/gen/prefab-home-01.jpg",
    highlights: ["Open studio plan", "Kitchenette + bath", "Factory-finished interiors", "Veranda option"],
    description: "A compact, factory-finished studio for landowners and small-format living — delivered complete, ready to use.",
  },
  {
    code: "NV-PH-02", slug: "1bhk-modular-home", name: "1BHK Modular Home", category: "prefab-homes",
    size_range: "450 – 750 sq ft", form: "1BHK", price_range: "₹18 L – ₹32 L", deployment: "45–75 days",
    use_cases: ["Weekend home", "Senior living", "Starter villa"], segments: ["B2C"],
    image: "/images/gen/prefab-home-02.jpg",
    highlights: ["1 bedroom layout", "Living + kitchen", "Insulated envelope", "Deck integration"],
    description: "A premium 1BHK modular home for weekend retreats and starter villas — built in the factory, installed in days.",
  },
  {
    code: "NV-PH-03", slug: "2bhk-family-home", name: "2BHK Family Home", category: "prefab-homes",
    size_range: "750 – 1,200 sq ft", form: "2BHK", price_range: "₹28 L – ₹55 L", deployment: "60–90 days",
    use_cases: ["Family home", "Farmhouse", "Holiday home"], segments: ["B2C"],
    image: "/images/gen/prefab-home-03.jpg",
    highlights: ["2 bed + 2 bath", "Double-height living option", "Premium façade palettes", "All-terrain foundations"],
    description: "A full family residence with luxury finishes — 80–90% built inside our factory for uncompromised quality.",
  },
  {
    code: "NV-PH-04", slug: "3bhk-signature-villa", name: "3BHK Signature Villa", category: "prefab-homes",
    size_range: "1,200 – 2,200 sq ft", form: "3BHK Villa", price_range: "₹48 L – ₹95 L", deployment: "75–90 days",
    use_cases: ["Primary residence", "Estate villa", "Luxury farmhouse"], segments: ["B2C"],
    image: "/images/gen/prefab-home-04.jpg",
    highlights: ["Cantilevered volumes", "Floor-to-ceiling glazing", "Designer interior package", "Smart-home ready"],
    description: "Our flagship villa format — sculptural massing, dramatic glass and hotel-grade interiors, order-ready.",
  },
  {
    code: "NV-PH-05", slug: "a-frame-cabin", name: "A-Frame Cabin", category: "prefab-homes",
    size_range: "350 – 900 sq ft", form: "A-Frame", price_range: "₹16 L – ₹45 L", deployment: "45–75 days",
    use_cases: ["Hill retreat", "Forest stay", "Farm cabin"], segments: ["B2C", "B2B"],
    image: "/images/gen/prefab-home-05.jpg",
    highlights: ["Iconic pitched silhouette", "Full glass gable", "Snow & rain optimized", "Mezzanine loft option"],
    description: "The icon of retreat living — a pitched-roof cabin with a glowing glass gable, nature-ready out of the box.",
  },
  {
    code: "NV-PH-06", slug: "barn-house", name: "Barn House", category: "prefab-homes",
    size_range: "900 – 1,800 sq ft", form: "Barn", price_range: "₹38 L – ₹80 L", deployment: "60–90 days",
    use_cases: ["Country home", "Event venue", "Estate guest house"], segments: ["B2C", "B2B"],
    image: "/images/gen/prefab-home-06.jpg",
    highlights: ["Vaulted great room", "Dark timber cladding", "Wide veranda", "Expandable wings"],
    description: "A modern barn typology with soaring interior volume — rural character, factory precision.",
  },

  // ================= 02 · PRECAST VILLAS =================
  {
    code: "NV-PV-01", slug: "precast-courtyard-villa", name: "Precast Courtyard Villa", category: "precast-villas",
    size_range: "1,500 – 2,800 sq ft", form: "Courtyard villa", price_range: "₹65 L – ₹1.4 Cr", deployment: "90–120 days",
    use_cases: ["Estate home", "Gated community", "Second home"], segments: ["B2C", "B2B"],
    image: "/images/gen/precast-villa-01.jpg",
    highlights: ["Board-formed concrete", "Central courtyard", "Thermal mass comfort", "50+ year lifespan"],
    description: "A sculptural precast villa wrapped around a private courtyard — concrete permanence at factory speed.",
  },
  {
    code: "NV-PV-02", slug: "precast-twin-villa", name: "Precast Twin Villa", category: "precast-villas",
    size_range: "2 × 1,200 sq ft", form: "Twin villa", price_range: "₹95 L – ₹1.8 Cr", deployment: "90–120 days",
    use_cases: ["Duplex development", "Family compound", "Rental asset"], segments: ["B2B"],
    image: "/images/gen/precast-villa-02.jpg",
    highlights: ["Mirrored twin plan", "Shared services core", "Developer-ready", "Acoustic separation"],
    description: "A mirrored two-unit villa system designed for developers building premium duplex inventory.",
  },
  {
    code: "NV-PV-03", slug: "panelized-villa-shell", name: "Panelized Villa Shell", category: "precast-villas",
    size_range: "1,000 – 3,000 sq ft", form: "Panelized shell", price_range: "₹1,900 – ₹2,600 / sq ft", deployment: "60–90 days",
    use_cases: ["Custom villa", "Architect-led project", "Hybrid build"], segments: ["B2B"],
    image: "/images/gen/precast-villa-03.jpg",
    highlights: ["Open-spec shell", "Architect friendly", "Rapid dry-in", "Any finish package"],
    description: "A precision panelized shell system — your architect designs, our factory delivers the envelope.",
  },
  {
    code: "NV-PV-04", slug: "lgsf-hill-villa", name: "LGSF Hill Villa", category: "precast-villas",
    size_range: "800 – 2,000 sq ft", form: "LGSF villa", price_range: "₹35 L – ₹85 L", deployment: "60–90 days",
    use_cases: ["Hill station home", "Slope sites", "Seismic zones"], segments: ["B2C", "B2B"],
    image: "/images/gen/precast-villa-04.jpg",
    highlights: ["Light gauge steel frame", "Slope-adaptive stilts", "Seismic-rated", "Rockwool insulated"],
    description: "Engineered for the hills — a light gauge steel villa that lands gently on slopes and shrugs off winters.",
  },
  {
    code: "NV-PV-05", slug: "precast-farm-estate", name: "Precast Farm Estate", category: "precast-villas",
    size_range: "2,500 – 5,000 sq ft", form: "Estate", price_range: "₹1.2 Cr – ₹2.5 Cr", deployment: "120–150 days",
    use_cases: ["Farm estate", "Wedding venue", "Legacy home"], segments: ["B2C"],
    image: "/images/gen/precast-villa-05.jpg",
    highlights: ["Multi-pavilion masterplan", "Pool + deck integration", "Staff quarters module", "Landscape package"],
    description: "A multi-pavilion estate composed from precast volumes — the grandest expression of the NUVUN platform.",
  },
  {
    code: "NV-PV-06", slug: "community-housing-block", name: "Community Housing Block", category: "precast-villas",
    size_range: "8 – 64 units", form: "Housing block", price_range: "Project-based", deployment: "120–180 days",
    use_cases: ["Township", "Workforce housing", "Institutional campus"], segments: ["B2B"],
    image: "/images/gen/precast-villa-06.jpg",
    highlights: ["Repeatable unit design", "Stackable to G+3", "Code-compliant", "Volume pricing"],
    description: "Scalable precast housing for townships and campuses — dignified, durable and delivered on schedule.",
  },

  // ================= 03 · SPACE CAPSULES =================
  {
    code: "NV-SC-01", slug: "horizon-space-capsule", name: "Horizon Space Capsule", category: "space-capsules",
    size_range: "180 – 320 sq ft", form: "Capsule", price_range: "₹22 L – ₹42 L", deployment: "30–45 days",
    use_cases: ["Glamping unit", "View suite", "Backyard escape"], segments: ["B2C", "B2B"],
    image: "/images/gen/space-capsule-cat-01.jpg",
    highlights: ["Aerospace-grade shell", "Panoramic curved glass", "Plug-and-play install", "Off-grid option"],
    description: "Our signature capsule — an aerodynamic shell with a full-width glass nose that frames the horizon.",
  },
  {
    code: "NV-SC-02", slug: "stargazer-capsule", name: "Stargazer Capsule", category: "space-capsules",
    size_range: "200 – 350 sq ft", form: "Capsule", price_range: "₹26 L – ₹48 L", deployment: "30–45 days",
    use_cases: ["Stargazing stay", "Desert camp", "Mountain resort"], segments: ["B2B"],
    image: "/images/gen/space-capsule-cat-02.jpg",
    highlights: ["Skylight roof glazing", "Blackout automation", "Thermal comfort in extremes", "Resort-grade finish"],
    description: "A capsule with a glass ceiling — engineered for night-sky destinations and unforgettable check-ins.",
  },
  {
    code: "NV-SC-03", slug: "wellness-sleep-capsule", name: "Wellness Sleep Capsule", category: "space-capsules",
    size_range: "120 – 220 sq ft", form: "Compact capsule", price_range: "₹14 L – ₹26 L", deployment: "21–30 days",
    use_cases: ["Retreat nap suite", "Spa rest room", "Airport rest pod"], segments: ["B2B"],
    image: "/images/gen/space-capsule-cat-03.jpg",
    highlights: ["Acoustic cocoon", "Circadian lighting", "Compact footprint", "Fleet deployable"],
    description: "A focused rest module for wellness programs, transit hubs and spa circuits.",
  },
  {
    code: "NV-SC-04", slug: "couple-retreat-capsule", name: "Couple Retreat Capsule", category: "space-capsules",
    size_range: "250 – 450 sq ft", form: "Capsule suite", price_range: "₹30 L – ₹55 L", deployment: "30–60 days",
    use_cases: ["Honeymoon suite", "Weekend escape", "Premium inventory"], segments: ["B2C", "B2B"],
    image: "/images/gen/space-capsule-cat-04.jpg",
    highlights: ["Bed-to-view layout", "Private deck + tub option", "Mood lighting scenes", "Mini-bar module"],
    description: "A romance-first capsule for couple-led stays — the highest ADR unit in most NUVUN resorts.",
  },
  {
    code: "NV-SC-05", slug: "expandable-capsule-suite", name: "Expandable Capsule Suite", category: "space-capsules",
    size_range: "350 – 700 sq ft", form: "Expandable capsule", price_range: "₹42 L – ₹75 L", deployment: "45–60 days",
    use_cases: ["Family suite", "Villa extension", "Presidential pod"], segments: ["B2B"],
    image: "/images/gen/space-capsule-cat-05.jpg",
    highlights: ["Slide-out volumes", "Family-ready layout", "Twin-bath option", "Premium finishes"],
    description: "A capsule that grows — slide-out volumes create a family suite from a transportable core.",
  },
  {
    code: "NV-SC-06", slug: "cliff-edge-capsule", name: "Cliff-Edge Capsule", category: "space-capsules",
    size_range: "220 – 380 sq ft", form: "Cantilevered capsule", price_range: "₹38 L – ₹68 L", deployment: "45–75 days",
    use_cases: ["Cliff resort", "Valley view stay", "Signature suite"], segments: ["B2B"],
    image: "/images/gen/space-capsule-cat-06.jpg",
    highlights: ["Cantilever engineering", "Anchored steel chassis", "Glass floor panel option", "Wind-rated envelope"],
    description: "An engineering statement — a capsule cantilevered over the view, anchored to bedrock.",
  },
  {
    code: "NV-SC-07", slug: "space-capsule-n5", name: "Space Capsule N5", category: "space-capsules",
    size_range: "200 – 260 sq ft", form: "Capsule suite", price_range: "Starting from ₹11 L", deployment: "30–45 days",
    use_cases: ["Resort suite", "Boutique glamping", "Weekend escape"], segments: ["B2C", "B2B"],
    image: "/images/gen/space-capsule-n5-01.jpg",
    gallery: [
      "/images/gen/space-capsule-n5-01.jpg",
      "/images/gen/space-capsule-n5-02.jpg",
      "/images/gen/space-capsule-n5-03.jpg",
      "/images/gen/space-capsule-n5-04.jpg",
    ],
    highlights: ["Aerodynamic composite shell", "Panoramic curved glazing", "Integrated ambient lighting", "Rapid site-ready install"],
    description: "The N5 is a sculpted capsule with a full-width glass front — the signature glamping unit for high-view destinations.",
  },
  {
    code: "NV-SC-08", slug: "space-capsule-n7", name: "Space Capsule N7", category: "space-capsules",
    size_range: "280 – 360 sq ft", form: "Capsule suite", price_range: "Starting from ₹11 L", deployment: "35–50 days",
    use_cases: ["Premium resort inventory", "Cliff / valley view stay", "Signature retreat"], segments: ["B2C", "B2B"],
    image: "/images/gen/space-capsule-n7-01.jpg",
    gallery: [
      "/images/gen/space-capsule-n7-01.jpg",
      "/images/gen/space-capsule-n7-02.jpg",
      "/images/gen/space-capsule-n7-03.jpg",
      "/images/gen/space-capsule-n7-04.jpg",
    ],
    highlights: ["Extended-length capsule", "Full-height glazing wall", "Private deck-ready base", "Resort-grade finish package"],
    description: "The N7 extends the capsule footprint for a two-zone layout — bedroom, lounge and full-height view wall in a single flowing shell.",
  },

  // ================= 04 · APPLE PODS =================
  {
    code: "NV-AP-01", slug: "apple-pod-classic", name: "Apple Pod Classic", category: "apple-pods",
    size_range: "120 – 200 sq ft", form: "Micro pod", price_range: "₹9.5 L – ₹18 L", deployment: "14–21 days",
    use_cases: ["Garden room", "Guest pod", "Reading retreat"], segments: ["B2C"],
    image: "/images/gen/apple-pod-01.jpg",
    highlights: ["Signature circular window", "Curved composite shell", "Crane-in installation", "Zero site mess"],
    description: "The original apple-shaped pod — a sculptural micro-room with one perfect circular window on the world.",
  },
  {
    code: "NV-AP-02", slug: "apple-pod-grande", name: "Apple Pod Grande", category: "apple-pods",
    size_range: "200 – 350 sq ft", form: "Pod suite", price_range: "₹16 L – ₹30 L", deployment: "21–30 days",
    use_cases: ["Backyard studio", "Boutique stay", "Studio apartment"], segments: ["B2C", "B2B"],
    image: "/images/gen/apple-pod-02.jpg",
    highlights: ["Full bath + kitchenette", "Double glazed shell", "Underfloor heating option", "Deck ring option"],
    description: "The grande format adds a full bath and kitchenette — a complete dwelling in a sculptural shell.",
  },
  {
    code: "NV-AP-03", slug: "apple-garden-studio", name: "Apple Garden Studio", category: "apple-pods",
    size_range: "150 – 250 sq ft", form: "Studio pod", price_range: "₹12 L – ₹22 L", deployment: "14–21 days",
    use_cases: ["Artist studio", "Music room", "Home gym"], segments: ["B2C"],
    image: "/images/gen/apple-pod-03.jpg",
    highlights: ["North-light glazing", "Acoustic insulation", "Climate controlled", "Power & data ready"],
    description: "A creative sanctuary at the bottom of the garden — daylight-led and acoustically isolated.",
  },
  {
    code: "NV-AP-04", slug: "apple-work-pod", name: "Apple Work Pod", category: "apple-pods",
    size_range: "100 – 180 sq ft", form: "Work pod", price_range: "₹8.5 L – ₹16 L", deployment: "10–14 days",
    use_cases: ["Home office", "Consulting room", "Creator studio"], segments: ["B2C", "B2B"],
    image: "/images/gen/apple-pod-04.jpg",
    highlights: ["Ergonomic fit-out", "Video-call lighting", "Fibre-ready", "10-day install"],
    description: "The commute-killer — a fully fitted executive office, ten steps from your kitchen.",
  },
  {
    code: "NV-AP-05", slug: "apple-spa-pod", name: "Apple Spa Pod", category: "apple-pods",
    size_range: "150 – 280 sq ft", form: "Spa pod", price_range: "₹18 L – ₹34 L", deployment: "21–30 days",
    use_cases: ["Private spa", "Sauna suite", "Wellness add-on"], segments: ["B2C", "B2B"],
    image: "/images/gen/apple-pod-05.jpg",
    highlights: ["Sauna + rain shower", "Cedar interior", "Waterproof core", "Resort wellness ready"],
    description: "A self-contained spa experience — sauna, rain shower and relaxation lounge in one pod.",
  },
  {
    code: "NV-AP-06", slug: "apple-twin-pod", name: "Apple Twin Pod", category: "apple-pods",
    size_range: "2 × 150 sq ft", form: "Twin pod", price_range: "₹20 L – ₹36 L", deployment: "21–30 days",
    use_cases: ["Homestay pair", "His & hers studios", "Rental duo"], segments: ["B2C", "B2B"],
    image: "/images/gen/cat-apple-pods.jpg",
    highlights: ["Linked deck bridge", "Independent climates", "Shared services trench", "Paired pricing"],
    description: "Two pods, one composition — connected by a timber bridge deck for pairs and rental portfolios.",
  },
  {
    code: "NV-AP-07", slug: "apple-cabin", name: "Apple Cabin", category: "apple-pods",
    size_range: "220 – 320 sq ft", form: "Cabin pod", price_range: "Starting from ₹11 L", deployment: "21–35 days",
    use_cases: ["Boutique cabin", "Micro-home", "Turnkey resort unit"], segments: ["B2C", "B2B"],
    image: "/images/gen/apple-cabin-01.jpg",
    gallery: [
      "/images/gen/apple-cabin-01.jpg",
      "/images/gen/apple-cabin-02.jpg",
      "/images/gen/apple-cabin-03.jpg",
      "/images/gen/apple-cabin-04.jpg",
      "/images/gen/apple-cabin-05.jpg",
    ],
    highlights: ["Full living + bath + bar", "Sculpted composite shell", "Panoramic glass wall", "Turnkey interior package"],
    description: "The Apple Cabin is a complete micro-dwelling — bedroom, lounge, kitchenette and bath in a single sculptural shell, ready to place and live in.",
  },

  // ================= 05 · COMMERCIAL PODS =================
  {
    code: "NV-CP-01", slug: "modular-office-pod", name: "Modular Office Pod", category: "commercial-pods",
    size_range: "200 – 800 sq ft", form: "Office module", price_range: "₹14 L – ₹48 L", deployment: "21–45 days",
    use_cases: ["Satellite office", "Sales gallery", "Campus block"], segments: ["B2B"],
    image: "/images/gen/commercial-pod-01.jpg",
    highlights: ["Glass curtain walls", "Brandable façade", "HVAC + IT ready", "Stack & link modules"],
    description: "A glass-walled workspace module that lands on any plaza — branded, wired and working in weeks.",
  },
  {
    code: "NV-CP-02", slug: "retail-kiosk-pod", name: "Retail Kiosk Pod", category: "commercial-pods",
    size_range: "80 – 250 sq ft", form: "Kiosk", price_range: "₹6.5 L – ₹18 L", deployment: "14–21 days",
    use_cases: ["Brand kiosk", "Pop-up retail", "Ticketing / F&B counter"], segments: ["B2B"],
    image: "/images/gen/commercial-pod-02.jpg",
    highlights: ["Full brand wrap", "Roll-up service window", "Relocatable", "Fleet ordering"],
    description: "Retail that moves at the speed of your brand — kiosks that deploy, relocate and multiply.",
  },
  {
    code: "NV-CP-03", slug: "cafe-container-module", name: "Café Container Module", category: "commercial-pods",
    size_range: "160 – 640 sq ft", form: "Container café", price_range: "₹12 L – ₹42 L", deployment: "30–45 days",
    use_cases: ["Café", "QSR outlet", "Cloud kitchen front"], segments: ["B2B"],
    image: "/images/gen/commercial-pod-03.jpg",
    highlights: ["Corten steel shell", "Commercial kitchen core", "Glass storefront", "Outdoor seating kit"],
    description: "A café in a box — corten steel, glass storefront and a commercial kitchen core, opening-day ready.",
  },
  {
    code: "NV-CP-04", slug: "cloud-kitchen-module", name: "Cloud Kitchen Module", category: "commercial-pods",
    size_range: "200 – 600 sq ft", form: "Kitchen module", price_range: "₹15 L – ₹40 L", deployment: "30–45 days",
    use_cases: ["Cloud kitchen", "Central commissary", "Resort F&B back-of-house"], segments: ["B2B"],
    image: "/images/gen/commercial-pod-04.jpg",
    highlights: ["FSSAI-spec surfaces", "Exhaust + fire suppression", "Multi-brand bays", "Utility skid included"],
    description: "Compliance-grade kitchen infrastructure that deploys behind any brand in under six weeks.",
  },
  {
    code: "NV-CP-05", slug: "clinic-wellness-module", name: "Clinic & Wellness Module", category: "commercial-pods",
    size_range: "250 – 900 sq ft", form: "Clinic module", price_range: "₹18 L – ₹60 L", deployment: "30–60 days",
    use_cases: ["Clinic", "Diagnostics point", "Salon / wellness room"], segments: ["B2B"],
    image: "/images/gen/commercial-pod-05.jpg",
    highlights: ["Hygienic wall systems", "Medical power layout", "Accessible design", "Rural deployable"],
    description: "Healthcare-grade modular rooms that bring clinics and wellness services anywhere they're needed.",
  },
  {
    code: "NV-CP-06", slug: "site-office-module", name: "Site Office & Utility Module", category: "commercial-pods",
    size_range: "120 – 600 sq ft", form: "Site module", price_range: "₹4.5 L – ₹24 L", deployment: "7–21 days",
    use_cases: ["Site office", "Security cabin", "Dormitory / toilet block"], segments: ["B2B"],
    image: "/images/gen/commercial-pod-06.jpg",
    highlights: ["Rugged steel chassis", "Rapid redeployment", "Solar option", "Volume fleet pricing"],
    description: "Site-grade infrastructure — offices, cabins and utility blocks that follow your projects around the country.",
  },

  // ================= 06 · HOSPITALITY & RESORT =================
  {
    code: "NV-HR-01", slug: "resort-room-cluster", name: "Resort Room Cluster", category: "hospitality-resort",
    size_range: "8 – 40 keys", form: "Room cluster", price_range: "₹28 L – ₹45 L / key", deployment: "90–150 days",
    use_cases: ["Boutique resort", "Hotel expansion", "Destination wedding venue"], segments: ["B2B"],
    image: "/images/gen/hospitality-01.jpg",
    highlights: ["Masterplanned clusters", "Uniform brand finish", "Phased delivery", "Revenue in 5 months"],
    description: "Complete room inventory, masterplanned and factory-built — the fastest route from land to revenue.",
  },
  {
    code: "NV-HR-02", slug: "glamping-village-kit", name: "Glamping Village Kit", category: "hospitality-resort",
    size_range: "6 – 24 units", form: "Glamping village", price_range: "₹18 L – ₹35 L / unit", deployment: "60–90 days",
    use_cases: ["Glamping resort", "Eco retreat", "Adventure camp"], segments: ["B2B"],
    image: "/images/gen/hospitality-02.jpg",
    highlights: ["Mixed unit typologies", "Minimal land disturbance", "Off-grid packages", "Common-area modules"],
    description: "A complete glamping destination in kit form — capsules, A-frames and common areas, phased to your ramp.",
  },
  {
    code: "NV-HR-03", slug: "lakeside-float-suite", name: "Lakeside Float Suite", category: "hospitality-resort",
    size_range: "300 – 550 sq ft", form: "Floating suite", price_range: "₹45 L – ₹85 L", deployment: "75–120 days",
    use_cases: ["Lake resort", "Backwater stay", "Signature suite"], segments: ["B2B"],
    image: "/images/gen/hospitality-03.jpg",
    highlights: ["Marine-grade pontoon", "Wraparound deck", "Water-reflection lighting", "Anchored mooring"],
    description: "A suite that sits on the water — marine-grade engineering beneath, five-star calm above.",
  },
  {
    code: "NV-HR-04", slug: "treeline-elevated-cabin", name: "Treeline Elevated Cabin", category: "hospitality-resort",
    size_range: "250 – 750 sq ft", form: "Elevated cabin", price_range: "₹32 L – ₹65 L", deployment: "60–90 days",
    use_cases: ["Forest resort", "Canopy stay", "Nature retreat"], segments: ["B2B"],
    image: "/images/gen/hospitality-04.jpg",
    highlights: ["Stilted steel structure", "Zero-cut foundations", "Canopy-level decks", "Dark-sky lighting"],
    description: "Cabins raised into the treeline on minimal-impact stilts — the forest stays untouched, guests stay enchanted.",
  },
  {
    code: "NV-HR-05", slug: "wellness-retreat-pavilion", name: "Wellness Retreat Pavilion", category: "hospitality-resort",
    size_range: "400 – 1,200 sq ft", form: "Pavilion", price_range: "₹40 L – ₹1.1 Cr", deployment: "75–120 days",
    use_cases: ["Yoga shala", "Spa pavilion", "Meditation hall"], segments: ["B2B"],
    image: "/images/gen/hospitality-05.jpg",
    highlights: ["Column-free span", "Biophilic materials", "Acoustic serenity", "Water-court option"],
    description: "Column-free pavilions for yoga, spa and stillness — the architectural heart of a wellness destination.",
  },
  {
    code: "NV-HR-06", slug: "presidential-pool-villa", name: "Presidential Pool Villa", category: "hospitality-resort",
    size_range: "1,200 – 2,500 sq ft", form: "Pool villa", price_range: "₹95 L – ₹2.2 Cr", deployment: "120–150 days",
    use_cases: ["Presidential suite", "Ultra-luxury inventory", "Private buyout villa"], segments: ["B2B"],
    image: "/images/gen/hospitality-06.jpg",
    highlights: ["Private infinity pool", "Butler pantry module", "Indoor-outdoor living", "Highest ADR format"],
    description: "The crown of the collection — a factory-built presidential villa with private pool and uncompromised luxury.",
  },
];

// Eight ways to build — the platform map (reference: One platform. Eight ways to build.)
export const BUILD_WAYS = [
  { n: "01", name: "Living Pods", desc: "Smart, compact & ready-to-live pods.", to: "/products/category/space-capsules" },
  { n: "02", name: "Prefab Homes", desc: "Beautiful homes, built in less time.", to: "/products/category/prefab-homes" },
  { n: "03", name: "A-Frame Series", desc: "Iconic designs. Nature-ready living.", to: "/products/a-frame-cabin" },
  { n: "04", name: "Container Series", desc: "Sustainable. Strong. Stylish.", to: "/products/cafe-container-module" },
  { n: "05", name: "Precast / Panelized", desc: "High-performance precast solutions.", to: "/products/category/precast-villas" },
  { n: "06", name: "Commercial Modules", desc: "Offices, studios & retail spaces.", to: "/products/category/commercial-pods" },
  { n: "07", name: "Pod-ect Modules", desc: "Modular products for every purpose.", to: "/products/category/apple-pods" },
  { n: "08", name: "Hospitality Clusters", desc: "Resorts & retreats, reimagined.", to: "/products/category/hospitality-resort" },
];

export function getCategories() {
  const counts = {};
  PRODUCTS.forEach((p) => { counts[p.category] = (counts[p.category] || 0) + 1; });
  return PRODUCT_CATEGORIES.map((c) => ({ ...c, product_count: counts[c.slug] || 0 }));
}

export function getProducts({ category, segment } = {}) {
  return PRODUCTS.filter((p) => {
    if (category && p.category !== category) return false;
    if (segment && !p.segments.includes(segment)) return false;
    return true;
  });
}

// Category image galleries — used to compose per-product multi-image galleries
const CATEGORY_GALLERIES = {
  "prefab-homes": ["/images/gen/prefab-home-08.jpg", "/images/gen/prefab-home-09.jpg", "/images/gen/prefab-home-07.jpg", "/images/gen/prefab-home-04.jpg"],
  "precast-villas": ["/images/gen/precast-villa-07.jpg", "/images/gen/precast-villa-01.jpg", "/images/gen/precast-villa-05.jpg", "/images/gen/precast-villa-04.jpg"],
  "space-capsules": ["/images/gen/space-capsule-cat-07.jpg", "/images/gen/space-capsule-cat-08.jpg", "/images/gen/space-capsule-cat-09.jpg", "/images/gen/space-capsule-cat-01.jpg"],
  "apple-pods": ["/images/gen/cat-apple-pods.jpg", "/images/gen/interior-luxury.jpg", "/images/gen/sustainability-home.jpg", "/images/gen/aframe-forest.jpg"],
  "commercial-pods": ["/images/gen/commercial-pod-07.jpg", "/images/gen/commercial-pod-01.jpg", "/images/gen/commercial-pod-03.jpg", "/images/gen/commercial-pod-05.jpg"],
  "hospitality-resort": [
    "/images/gen/hospitality-09.jpg",
    "/images/gen/hospitality-extra-01.jpg",
    "/images/gen/hospitality-extra-02.jpg",
    "/images/gen/hospitality-extra-03.jpg",
    "/images/gen/hospitality-extra-04.jpg",
    "/images/gen/hospitality-extra-05.jpg",
    "/images/gen/hospitality-extra-06.jpg",
    "/images/gen/hospitality-extra-07.jpg",
    "/images/gen/hospitality-extra-08.jpg",
    "/images/gen/hospitality-extra-09.jpg",
    "/images/gen/hospitality-07.jpg",
    "/images/gen/hospitality-08.jpg",
    "/images/gen/hospitality-10.jpg",
  ],
};

export function getProduct(slug) {
  const p = PRODUCTS.find((x) => x.slug === slug);
  if (!p) return null;
  // If the product defines its own gallery, honor it verbatim (no category fill-in)
  if (Array.isArray(p.gallery)) return { ...p };
  const gallery = [p.image, ...(CATEGORY_GALLERIES[p.category] || [])]
    .filter((v, i, a) => a.indexOf(v) === i)
    .slice(0, 12);
  return { ...p, gallery };
}
